/**
 * SimulationEngine - движок симуляции муравья Лэнгтона
 * Requirements: 2.1, 2.2, 2.3
 */

import type { 
  SimulationState, 
  SimulationConfig 
} from './types.js';
import type { ISimulationEngine, IGridNavigator } from './interfaces.js';

/**
 * SimulationEngine реализует логику муравья Лэнгтона:
 * 1. Поворот по правилу для текущего цвета ячейки (Req 2.1)
 * 2. Смена цвета ячейки на следующий в палитре (Req 2.2)
 * 3. Движение вперёд на одну клетку (Req 2.3)
 */
export class SimulationEngine implements ISimulationEngine {
  private state: SimulationState;
  private readonly config: SimulationConfig;
  private readonly navigator: IGridNavigator;

  constructor(config: SimulationConfig, navigator: IGridNavigator) {
    this.config = config;
    this.navigator = navigator;
    this.state = this.createInitialState();
  }

  /**
   * Выполнить один шаг симуляции
   * Req 2.1: Поворот по правилу для текущего цвета
   * Req 2.2: Смена цвета на следующий
   * Req 2.3: Движение вперёд
   */
  step(): void {
    const { ant, grid } = this.state;
    const { x, y } = ant.position;
    
    // Получить текущий цвет ячейки
    const currentColorIndex = grid[y][x];
    
    // Req 2.1: Поворот по правилу для текущего цвета
    // Передаём позицию для треугольной сетки (поворот зависит от ориентации треугольника)
    const turnAmount = this.config.ruleSet[currentColorIndex];
    const newDirection = this.navigator.turn(ant.direction, turnAmount, ant.position);
    
    // Req 2.2: Смена цвета на следующий в палитре (циклически)
    const nextColorIndex = (currentColorIndex + 1) % this.config.colors.length;
    grid[y][x] = nextColorIndex;
    
    // Req 2.3: Движение вперёд
    const moveResult = this.navigator.move(ant.position, newDirection);
    
    // Обновить состояние муравья
    this.state.ant = {
      position: moveResult.position,
      direction: moveResult.direction
    };
    
    this.state.stepCount++;
  }

  /**
   * Получить текущее состояние симуляции
   */
  getState(): SimulationState {
    return {
      grid: this.state.grid.map(row => [...row]),
      ant: {
        position: { ...this.state.ant.position },
        direction: this.state.ant.direction
      },
      stepCount: this.state.stepCount
    };
  }

  /**
   * Установить состояние симуляции
   */
  setState(state: SimulationState): void {
    this.state = {
      grid: state.grid.map(row => [...row]),
      ant: {
        position: { ...state.ant.position },
        direction: state.ant.direction
      },
      stepCount: state.stepCount
    };
  }

  /**
   * Сбросить симуляцию в начальное состояние
   */
  reset(): void {
    this.state = this.createInitialState();
  }

  /**
   * Создать начальное состояние симуляции
   */
  private createInitialState(): SimulationState {
    // Создать пустую сетку (все ячейки цвета 0)
    const grid: number[][] = Array.from(
      { length: this.config.height },
      () => Array(this.config.width).fill(0)
    );

    return {
      grid,
      ant: {
        position: { ...this.config.startPosition },
        direction: 0
      },
      stepCount: 0
    };
  }
}
